import React, { useState, useEffect } from 'react';
import { Container, Typography, FormControl, InputLabel, Select, MenuItem, Tabs, Tab, CircularProgress, Box, Avatar, List, ListItem, ListItemText } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import TopThreeFisherman from './TopThreeFisherman';

const BASE_URL = 'https://sandpaperdashboard.com/navarrefishingrodeo/api/';
const ENDPOINTS = {
  divisions: 'divisions/',
  leaderboards: (divisionId) => `divisions/${divisionId}/leaderboards/`,
  fishevents: 'fishevents/',
  sponsors: 'sponsors/',
};

const UserFacingLeaderboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [leaderboardResults, setLeaderboardResults] = useState([]);
  const [fishEvents, setFishEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topThreeModalOpen, setTopThreeModalOpen] = useState(false);
  const [selectedLeaderboard, setSelectedLeaderboard] = useState(null);

  useEffect(() => {
    fetchDivisions();
  }, []);

  useEffect(() => {
    if (selectedDivision) {
      fetchLeaderboardResults(selectedDivision.id);
      fetchFishEvents(selectedDivision.id);
    }
  }, [selectedDivision]);

  const fetchDivisions = async () => {
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.divisions}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      
      // Fetch sponsor details for each division
      const divisionsWithSponsors = await Promise.all(data.map(async (division) => {
        const sponsorDetails = await Promise.all(division.sponsors.map(async (sponsorId) => {
          const sponsorResponse = await fetch(`${BASE_URL}${ENDPOINTS.sponsors}${sponsorId}/`);
          if (!sponsorResponse.ok) throw new Error(`HTTP error! status: ${sponsorResponse.status}`);
          return sponsorResponse.json();
        }));
        return { ...division, sponsorDetails };
      }));

      setDivisions(divisionsWithSponsors);
      if (divisionsWithSponsors.length > 0) setSelectedDivision(divisionsWithSponsors[0]);
    } catch (error) {
      console.error('Error fetching divisions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboardResults = async (divisionId) => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}${ENDPOINTS.leaderboards(divisionId)}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const formattedData = data.leaderboards.map(leaderboard => ({
        ...leaderboard,
        id: leaderboard.id,
        sponsors: leaderboard.sponsors ? leaderboard.sponsors.map(s => s.name).join(', ') : 'N/A',
        fish: leaderboard.fish.map(fish => fish.name).join(', '),
        results: Array.isArray(leaderboard.results) ? leaderboard.results : []
      }));
      console.log("Formatted leaderboard data:", formattedData);
      setLeaderboardResults(formattedData);
    } catch (error) {
      console.error('Error fetching leaderboard results:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFishEvents = async (divisionId) => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}${ENDPOINTS.fishevents}?division=${divisionId}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setFishEvents(data);
    } catch (error) {
      console.error('Error fetching fish events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLeaderboardRowClick = (params) => {
    console.log("Clicked leaderboard:", params.row);
    setSelectedLeaderboard(params.row);
    setTopThreeModalOpen(true);
  };

  const handleTopThreeModalClose = () => {
    setTopThreeModalOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const LeaderboardResults = ({ results }) => (
    <List dense>
      {results && results.length > 0 ? (
        results.map((result, index) => (
          <ListItem key={index}>
            <ListItemText
              primary={`${index + 1}. ${result.fisherman || 'N/A'}`}
              secondary={`Total Weight: ${result.total_weight || 'N/A'} lbs`}
            />
          </ListItem>
        ))
      ) : (
        <ListItem>
          <ListItemText primary="No fishermen data available" />
        </ListItem>
      )}
    </List>
  );

  const leaderboardColumns = [
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'sponsors', headerName: 'Sponsors', width: 200 },
    { field: 'fish', headerName: 'Fish', width: 200 },
    // {
    //   field: 'results',
    //   headerName: 'Top 3 Fishermen',
    //   width: 300,
    //   renderCell: (params) => <LeaderboardResults results={params.row.results} />,
    // },
  ];

  const fishEventColumns = [
    { field: 'time_date', headerName: 'Date & Time', width: 200 },
    { field: 'weight', headerName: 'Weight', width: 100 },
    { field: 'fish', headerName: 'Fish', width: 150, valueGetter: (params) => params.value?.name || 'N/A' },
    { field: 'fisherman', headerName: 'Fisherman', width: 200, valueGetter: (params) => params.value ? `${params.value.firstname} ${params.value.lastname}` : 'N/A' },
  ];

  const renderHeader = () => {
    if (!selectedDivision || !selectedDivision.sponsorDetails || selectedDivision.sponsorDetails.length === 0) {
      return (
        <Typography variant="h4" component="h1" sx={{ marginBottom: '20px', textAlign: 'center' }}>
          Navarre Fishing Rodeo Leaderboard
        </Typography>
      );
    }

    return (
      <Box>
        <Typography variant="h4" component="h1" sx={{ marginBottom: '10px', textAlign: 'center' }}>
          Navarre Fishing Rodeo Leaderboard
        </Typography>
        <Typography variant="h6" component="h2" sx={{ marginBottom: '20px', textAlign: 'center' }}>
          {selectedDivision.name} - Sponsored By:
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
          {selectedDivision.sponsorDetails.map((sponsor) => (
            <Box key={sponsor.id} sx={{ m: 1, textAlign: 'center' }}>
              {sponsor.image && (
                <a href={sponsor.website} target="_blank" rel="noopener noreferrer">
                  <img
                    src={sponsor.image}
                    alt={sponsor.name}
                    style={{ width: 300, height: 'auto', maxHeight: 300, objectFit: 'contain' }}
                  />
                </a>
              )}
              <Typography variant="subtitle1">
                {sponsor.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Container style={{ marginTop: '20px' }}>
      {renderHeader()}
      <FormControl fullWidth style={{ marginBottom: '20px' }}>
        <InputLabel>Division</InputLabel>
        <Select
          value={selectedDivision?.id || ''}
          onChange={(e) => setSelectedDivision(divisions.find(d => d.id === e.target.value))}
        >
          {divisions.map((division) => (
            <MenuItem key={division.id} value={division.id}>
              <Box display="flex" alignItems="center" flexWrap="wrap">
                <Typography>{division.name}</Typography>
                {division.sponsorDetails && division.sponsorDetails.length > 0 && (
                  <Box ml={2} display="flex" alignItems="center" flexWrap="wrap">
                    {division.sponsorDetails.map((sponsor) => (
                      <Box key={sponsor.id} display="flex" alignItems="center" mr={1}>
                        {sponsor.image && (
                          <Avatar
                            src={sponsor.image}
                            alt={sponsor.name}
                            sx={{ width: 24, height: 24, mr: 0.5 }}
                          />
                        )}
                        <Typography variant="caption">{sponsor.name}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Tabs value={tabValue} onChange={handleTabChange} style={{ marginBottom: '20px' }}>
        <Tab label="Leaderboards" />
        <Tab label="Weigh Ins" />
      </Tabs>
      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid
          rows={tabValue === 0 ? leaderboardResults : fishEvents}
          columns={tabValue === 0 ? leaderboardColumns : fishEventColumns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 100]}
          autoHeight
          disableSelectionOnClick
          onRowClick={tabValue === 0 ? handleLeaderboardRowClick : undefined}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: tabValue === 0 ? 'pointer' : 'default',
            },
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: '#74B9BB',
            },
          }}
        />
      )}
      <TopThreeFisherman
        open={topThreeModalOpen}
        onClose={handleTopThreeModalClose}
        divisionId={selectedDivision?.id}
        selectedLeaderboard={selectedLeaderboard}
      />
    </Container>
  );
};

export default UserFacingLeaderboard;